import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModalControl } from "../../customeHooks/useModalControl";
import { CreatedEvent } from "../../redux/features/event/event.types";
import { useAppSelector } from "../../redux/hooks";
import {
  copyTextToClipboard,
  formatDate,
  getEventUrl,
} from "../../utils/helpers";
import { Button } from "../buttons/button";
import { Icon } from "../icon/icon";
import { ArchiveEventModal } from "../modal/app_modals/archiveEventModal";
import { DeleteEventModal } from "../modal/app_modals/deleteEventModal";
import { DuplicateEventModal } from "../modal/app_modals/duplicateEventModal";
import { EventCard } from "./eventCard";

const getVirtualLocationLabel = (link?: string) => {
  if (!link) return "Virtual Event";
  const lowerLink = link.toLowerCase();
  if (lowerLink.includes("meet.google.com") || lowerLink.includes("google")) {
    return "Google Meet";
  }
  if (lowerLink.includes("zoom")) {
    return "Zoom";
  }
  return "Virtual Event";
};

export const FullEventCard = ({
  event,
  idx,
  refetchEvents = () => {},
}: {
  event: CreatedEvent;
  idx?: number;
  refetchEvents: () => void;
}) => {
  const navigate = useNavigate();

  const { modalOpen, handleModalOpen, handleModalClose, modal } =
    useModalControl();

  const [buttonText, setButtonText] = useState("Copy Event Link");

  const { isSignedIn } = useAppSelector((state) => state.auth);

  const organizerName = event.createdBy?.isOrganisation
    ? event.createdBy?.organisation?.name
    : `${event.createdBy?.firstName ?? ""} ${event.createdBy?.lastName ?? ""}`.trim();

  const mobileLocation =
    event.type === "physical"
      ? event.location?.split(",")[0]?.trim() || event.location
      : getVirtualLocationLabel(event.location);

  const mobileDateTime = `${formatDate(event.startDate, "ddd, MMM D", true)}, ${event.startTime ?? ""}`;

  const handleCopy = async () => {
    await copyTextToClipboard(getEventUrl(event.slug));
    setButtonText("Copied!");
    setTimeout(() => {
      setButtonText("Copy Event Link");
    }, 2000);
  };

  const handleCardClick = () => {
    if (event.status === "draft" && event.userType === "organiser") {
      navigate(`/event/${encodeURIComponent(event.slug)}/edit`, {
        replace: true,
      });
    } else if (event.userType === "organiser") {
      navigate(`/events/${encodeURIComponent(event.slug)}/details`);
    } else if (event.userType === "guest" && isSignedIn) {
      navigate(`/events/guest/${event.guestId}/ticket`);
    } else if (event.userType === "guest" && !isSignedIn) {
      navigate(`/events/${idx}/ticket/guest`);
    } else {
      navigate(`/events/${encodeURIComponent(event.slug)}/preview`);
    }
  };

  return (
    <>
      <div className="flex gap-[2px]">
        <EventCard
          image={event.image}
          name={event.name}
          location={event.location}
          startDate={formatDate(event.startDate, "ddd, MMMM DD, YYYY", true)}
          endDate={formatDate(event.endDate, "ddd, MMMM DD, YYYY", true)}
          startTime={event.startTime}
          endTime={event.endTime}
          event={event}
          noTickets={event.ticketCount ?? 0}
          totalGuests={event.totalGuests}
          isOrganizer={event.userType === "organiser"}
          className=""
          clickable
          onClick={() => {
            handleCardClick();
          }}
          showStatusPill={["draft", "cancelled"].includes(event.status)}
          mobileCard
          organizerName={organizerName}
          mobileLocation={mobileLocation}
          mobileDateTime={mobileDateTime}
        >
          <div className="grid w-full grid-cols-1 gap-2.5 lg:flex-1 lg:grid-cols-2 lg:items-end">
            {/* if is organizer and event is draft  */}
            {event.status === "draft" && event.userType === "organiser" ? (
              <>
                <Button
                  type="button"
                  icon="pencil"
                  text="Edit Event"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/event/${event.slug}/edit`, { replace: true });
                  }}
                  variation="plain-grey"
                />

                <Button
                  type="button"
                  icon="bin-2"
                  iconClassName=""
                  text="Delete Draft"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalOpen("deleteEventModal");
                  }}
                  variation="plain-grey"
                />
              </>
            ) : undefined}

            {/* if is guest  */}
            {event.userType === "guest" ? (
              <>
                <Button
                  type="button"
                  icon="download"
                  text="Download Ticket"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSignedIn) {
                      navigate(`/events/guest/${event.guestId}/ticket`);
                    } else {
                      navigate(`/events/${idx}/ticket/guest`);
                    }
                  }}
                  variation="plain-grey"
                />

                <Button
                  type="button"
                  icon="link"
                  text={buttonText}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy();
                  }}
                  variation="plain-grey"
                />
              </>
            ) : undefined}

            {/* If is organiser and event is past  */}
            {["completed", "cancelled"].includes(event.status) &&
            event.userType === "organiser" ? (
              <>
                <Button
                  type="button"
                  icon="copy"
                  text="Duplicate Event"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalOpen("duplicateEventModal");
                  }}
                  variation="plain-grey"
                />

                <Button
                  type="button"
                  icon="archive"
                  text={"Archive Event"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalOpen("archiveEventModal");
                  }}
                  variation="plain-grey"
                />
              </>
            ) : undefined}

            {/* if is organizer and is upcoming event  */}
            {event.userType === "organiser" && event.status === "upcoming" ? (
              <>
                <Button
                  type="button"
                  icon="arrow-right"
                  iconClassName="rotate-[-45deg]"
                  text="View Event"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/events/${encodeURIComponent(event?.slug ?? "")}/preview`,
                      {
                        replace: true,
                      },
                    );
                  }}
                  variation="plain-grey"
                />

                <Button
                  type="button"
                  icon="link"
                  text={buttonText}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy();
                  }}
                  variation="plain-grey"
                />
              </>
            ) : undefined}
          </div>
        </EventCard>

        <button
          type="button"
          className="soft-shrink hidden !min-h-full w-[82px] max-w-[82px] items-center justify-center rounded-2xl bg-white p-3 md:flex"
          onClick={handleCardClick}
        >
          <Icon
            svg="caret-down"
            width={24}
            height={24}
            className="-rotate-90"
          />
        </button>
      </div>

      <DeleteEventModal
        open={modalOpen && modal === "deleteEventModal"}
        handleClose={() => {
          handleModalClose();
        }}
        event={event}
        onSuccess={() => {
          handleModalClose();
          refetchEvents();
        }}
      />

      <DuplicateEventModal
        open={modalOpen && modal === "duplicateEventModal"}
        handleClose={() => {
          handleModalClose();
        }}
        event={event}
        onSuccess={() => {
          handleModalClose();
          navigate("/events?EventStatus=draft");
        }}
      />

      <ArchiveEventModal
        open={modalOpen && modal === "archiveEventModal"}
        handleClose={() => {
          handleModalClose();
        }}
        event={event}
        onSuccess={() => {
          handleModalClose();
          refetchEvents();
        }}
      />
    </>
  );
};
